import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { login } from "../../../data";
import AuthStore from "../../../stores/AuthStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Chrome, Eye, EyeOff } from "lucide-react";
import { AuthLayout } from "../shared/layout";
import { useToast } from "@/components/ui/use-toast";

export function LoginScreen() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const {
    mutate: loginUser,
    isLoading,
  } = useMutation({
    mutationKey: "login",
    mutationFn: (data) => login(data),
    onSuccess: (response) => {
      AuthStore.setUser(response.data.token);
      navigate("/dashboard");
      toast({
        title: t("auth.welcomeBack"),
        description: t("auth.loginSuccess"),
      });
    },
    onError: (error) => {
      const errorCode = error.response?.data?.code;
      const errorMessage = error.response?.data?.message || t("auth.loginFailed");
      
      // Handle specific error codes
      if (errorCode === 'SOCIAL_LOGIN_REQUIRED') {
        toast({
          title: t("auth.socialLoginRequired"),
          description: errorMessage,
          variant: "default",
          action: (
            <Button variant="outline" size="sm" onClick={handleGoogleLogin}>
              {t("auth.google")}
            </Button>
          ),
        });
        return;
      }
      
      toast({
        title: t("common.error"),
        description: errorMessage,
        variant: "destructive",
      });
    }
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    if (token) {
      AuthStore.setUser(token);
      navigate("/dashboard");
    }
  }, [location, navigate]);

  const onSubmit = (data) => {
    loginUser(data);
  };

  const handleGoogleLogin = () => {
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/google`;
  };

  return (
    <AuthLayout 
      title={t("auth.signInTitle")} 
      description={t("auth.signInSubtitle")}
    >
      <div className="grid gap-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Controller
                name="email"
                control={control}
                rules={{ required: t("errors.required") }}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="email"
                    placeholder="name@example.com"
                    type="email"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect="off"
                    disabled={isLoading}
                  />
                )}
              />
              {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
            </div>
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-muted-foreground hover:underline"
                >
                  {t("auth.forgotPassword")}
                </Link>
              </div>
              <div className="relative">
                <Controller
                  name="password"
                  control={control}
                  rules={{ required: t("errors.required") }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      id="password"
                      type={showPassword ? "text" : "password"}
                      disabled={isLoading}
                    />
                  )}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <Eye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
              {errors.password && <p className="text-sm text-red-500">{errors.password.message}</p>}
            </div>
            <Button disabled={isLoading}>
              {isLoading && (
                <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
              )}
              {t("auth.signInWithEmail")}
            </Button>
          </div>
        </form>
        
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              {t("auth.orContinueWith")}
            </span>
          </div>
        </div>
        
        <Button variant="outline" type="button" disabled={isLoading} onClick={handleGoogleLogin}>
          {isLoading ? (
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          ) : (
            <Chrome className="mr-2 h-4 w-4" />
          )}
          {t("auth.google")}
        </Button>

        <p className="px-8 text-center text-sm text-muted-foreground">
          {t("auth.dontHaveAccount")}{" "}
          <Link
            to="/register"
            className="underline underline-offset-4 hover:text-primary"
          >
            {t("auth.signUp")}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
